// Ops · Payouts: per-partner statements for a period, the payout snapshot, paid and void
// (docs/PHASE7-CONTRACT.md, contract.d.ts "Ops · Payouts"). Pure: node --test runs it.

import { FIRST_PARTY_LABEL, isFirstParty } from '../../lib/format.js';

/** @typedef {import('@popcart/core/v1').PayoutStatement} PayoutStatement */
/** @typedef {import('@popcart/core/v1').ShopPayout} ShopPayout */
/** @typedef {import('@popcart/core/v1').PopCartApi} PopCartApi */
/** @typedef {{ from: string; to: string }} Period */

/** @param {Period} p */
export const payoutsKey = (p) => ['ops', 'payouts', p.from, p.to];

/** @type {Record<ShopPayout['status'], { label: string; tone: 'warning' | 'success' | 'neutral' }>} */
export const PAYOUT_PILL = {
  pending: { label: 'To pay', tone: 'warning' },
  paid: { label: 'Paid', tone: 'success' },
  void: { label: 'Void', tone: 'neutral' },
};

/** 1500 bps → "15%", 1250 → "12.5%". @param {number} bps */
export function bpsLabel(bps) {
  if (!Number.isFinite(bps)) return '—';
  return `${Math.round(bps) / 100}%`;
}

/**
 * The rate line under a statement's commission: "15%", or "First-party · no commission" for a
 * first-party shop (its commission is 0 by design, not a missing rate).
 * @param {Pick<PayoutStatement, 'commissionBps'> & { firstParty?: boolean }} s
 */
export function commissionRateLabel(s) {
  return isFirstParty(s) ? FIRST_PARTY_LABEL : bpsLabel(s.commissionBps);
}

/** "GCash", "Bank" … from the shop's payout method, or a nudge when there's none. @param {string | null} method */
export function payoutMethodLabel(method) {
  if (!method) return 'No payout method yet';
  const m = method.toLowerCase();
  if (m === 'gcash') return 'GCash';
  if (m === 'maya') return 'Maya';
  if (m === 'bank') return 'Bank transfer';
  return method;
}

/**
 * The live (not void) payout for this shop and exactly this period, if any.
 * @param {readonly ShopPayout[]} payouts
 * @param {string} shopId
 * @param {Period} p
 */
export function livePayoutFor(payouts, shopId, p) {
  return payouts.find((x) => x.shopId === shopId && x.from === p.from && x.to === p.to && x.status !== 'void') ?? null;
}

/**
 * One row per statement with what Ops can do next: create the payout, pay it, or nothing (paid,
 * or nothing to pay).
 * @param {readonly PayoutStatement[]} statements
 * @param {readonly ShopPayout[]} payouts
 * @param {Period} p
 */
export function statementRows(statements, payouts, p) {
  return [...statements]
    .sort((a, b) => b.netCentavos - a.netCentavos || a.shopName.localeCompare(b.shopName))
    .map((s) => {
      const payout = livePayoutFor(payouts, s.shopId, p);
      /** @type {'create' | 'pay' | 'paid' | 'nothing'} */
      const next = payout ? (payout.status === 'paid' ? 'paid' : 'pay') : s.netCentavos > 0 ? 'create' : 'nothing';
      return { statement: s, payout, next };
    });
}

/** Period totals across the statements. @param {readonly PayoutStatement[]} statements */
export function statementTotals(statements) {
  return statements.reduce(
    (t, s) => ({
      ordersCount: t.ordersCount + s.ordersCount,
      grossCentavos: t.grossCentavos + s.grossCentavos,
      commissionCentavos: t.commissionCentavos + s.commissionCentavos,
      adjustmentsCentavos: t.adjustmentsCentavos + s.adjustmentsCentavos,
      netCentavos: t.netCentavos + s.netCentavos,
    }),
    { ordersCount: 0, grossCentavos: 0, commissionCentavos: 0, adjustmentsCentavos: 0, netCentavos: 0 },
  );
}

/** Pending first, then newest. @param {readonly ShopPayout[]} payouts */
export function sortPayouts(payouts) {
  const rank = { pending: 0, paid: 1, void: 2 };
  return [...payouts].sort((a, b) => rank[a.status] - rank[b.status] || Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export const REFERENCE_MAX = 120;

/** @param {string} reference */
export function validateReference(reference) {
  const r = String(reference ?? '').trim();
  if (r.length < 3) return { ok: /** @type {const} */ (false), error: 'Add the transfer reference, so the shop can match it.' };
  if (r.length > REFERENCE_MAX) return { ok: /** @type {const} */ (false), error: `Keep it under ${REFERENCE_MAX} characters.` };
  return { ok: /** @type {const} */ (true), value: r };
}

/**
 * "Create payout": snapshot this shop's statement for the period (409 if a live one exists).
 * @param {Pick<PopCartApi, 'createPayout'>} api
 * @param {string} shopId
 * @param {Period} p
 */
export function createPayoutAction(api, shopId, p) {
  return api.createPayout({ shopId, from: p.from, to: p.to });
}

/**
 * Mark paid with the reference.
 * @param {Pick<PopCartApi, 'markPayoutPaid'>} api
 * @param {string} payoutId
 * @param {string} reference
 */
export async function markPaidAction(api, payoutId, reference) {
  const r = validateReference(reference);
  if (!r.ok) return r;
  const payout = await api.markPayoutPaid(payoutId, r.value);
  return { ok: /** @type {const} */ (true), payout };
}

/**
 * Swap a changed payout into the cached response.
 * @param {{ statements: PayoutStatement[]; payouts: ShopPayout[] } & Period} data
 * @param {ShopPayout} payout
 */
export function withPayout(data, payout) {
  const has = data.payouts.some((x) => x.id === payout.id);
  return { ...data, payouts: has ? data.payouts.map((x) => (x.id === payout.id ? payout : x)) : [payout, ...data.payouts] };
}
